import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import isStandalone from "../../Utils/isStandalone";
import { useGuardedNavigate } from "../../contexts/NavigationGuardContext";
import { useBreadcrumbStackOptional } from "../contexts/BreadcrumbStackContext";

// Segment → i18n key. Flattened from the legacy `getLabel()` if-chain so new
// routes are a one-line addition. Segments needing conditional resolution
// (index 0 = Home, standalone "history") are handled in getLabel() below.
const SEGMENT_LABEL_KEYS = {
  schemes: "schemes.governmentSchemes",
  details: "schemes.breadcrumbSchemeDetails",
  apply: "schemes.breadcrumbApplyScheme",
  "grm-create": "agentGrm.GRM",
  "grm-application": "agentGrm.GRM_APPLICATION",
  "grievance-redressal": "agentGrm.GRM",
  "farmer-passbook": "FARMER_PASSBOOK",
  "soil-card": "soil_health_card",
  "soil-lab": "soil_health_labs",
  registration: "AGENT_FARMER_REGISTRATIONS",
  faq: "DFSWEB_FAQs",
  "key-contacts": "KeyContacts",
  "about-section": "DFSWEB_About_Bihar",
  "assets-section": "DFSWEB_ASSETS",
  "assets-details": "DFSWEB_ASSETS_details",
  // Horticulture service module (DFS-3670) — carried over from the legacy bar.
  horti: "HORTI_MODULE_TITLE",
  "provider-details": "Details",
  "enquiry-history": "HORTI_ENQUIRY_HISTORY",
  "application-details": "Details",
  applied: "Applied_Schemes",
  "farmer-registration": "Farmer_Registration",
  "recover-dbt-id": "Recover_DBT_ID",
  dashboards: "DFSWEB_DASHBOARD",
  "accessibility-statement": "AccessibilityStatement",
  login: "DFSWEB_LOGIN_PAGE",
};

// Trail type matches the Back pill's 14px so the whole bar reads at one size.
const CRUMB_TEXT_SX = {
  fontSize: "14px",
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

const RewampBreadCrumbsBar = (props) => {
  const { showBack = true, onBack, sx, extraCrumbLabel, hideSegments = [], useStackTrail } = props;
  // Normalised to an array so a page needing multiple trailing crumbs (e.g.
  // Assets' detail page: super-category > sub-category) can pass
  // extraCrumbLabel={[cat, subCat]} — single-string callers (AssetsPage's
  // own "> Storage") are unaffected. Entries may be a string (current /
  // non-clickable) or { label, to } (clickable link).
  const extraCrumbLabels = (extraCrumbLabel == null
    ? []
    : Array.isArray(extraCrumbLabel)
      ? extraCrumbLabel
      : [extraCrumbLabel]
  ).map((entry) =>
    typeof entry === "string" || entry == null
      ? { label: entry, to: null }
      : { label: entry.label, to: entry.to || null }
  );
  const location = useLocation();
  const navigate = useGuardedNavigate();
  const stack = useBreadcrumbStackOptional();
  const stackEntries = stack?.entries ?? [];
  const renderFromStack = useStackTrail !== false && stackEntries.length > 0;
  const { t } = useTranslation();
  const theme = useTheme();

  // primary.interactive already carries the light/dark flip; current crumb is that colour @50%.
  const linkColor = theme.palette.primary.interactive;
  const currentColor = alpha(linkColor, 0.5);

  // ── Path → crumb segments (legacy normalisation, unchanged) ───────────────
  let pathnames = location.pathname.split("/").filter((x) => x);
  if (["dashboards"].some((path) => pathnames?.includes(path))) {
    pathnames.shift();
  }
  if (!window?.contextPath) {
    pathnames = ["home", ...pathnames];
  }
  if (props?.trackScheme && props?.trackScheme?.length === 1) {
    pathnames = [...pathnames, ...props.trackScheme];
  }
  if (["complaintdetails", "inbox"].some((path) => pathnames?.includes(path))) {
    pathnames.splice(-1);
  }

  // Router state carried when navigating to/through the scheme "details" crumb.
  const detailsState = {
    schemeId: props.schemeId,
    level: props.level,
    startDate: props.startDate,
    endDate: props.endDate,
    active: props.active,
    mainId: props.mainSchemeId,
  };

  const getLabel = (segment, index) => {
    if (index === 0) return t("DFSWEB_HOME");
    if (isStandalone() && segment === "history") return t("DFSWEB_DASHBOARD");
    const key = SEGMENT_LABEL_KEYS[segment];
    if (key) return t(key);
    return t(segment.charAt(0).toUpperCase() + segment.slice(1));
  };

  // Cumulative route for the crumb at `index` (legacy route-building, unchanged).
  const buildRoute = (index) => {
    let startIndex = 0;
    if (!window.contextPath) {
      startIndex = pathnames?.length > 2 ? 1 : 0;
    }
    let routeTo = `/${pathnames.slice(startIndex, index + 1).join("/")}`;
    if (routeTo === "/grm") {
      routeTo = "/grm/inbox";
    }
    // Matches the header/footer "Assets" nav tag's target, not the bare listing route.
    if (pathnames[index] === "assets-section") {
      return `${window.contextPath || ""}/#assets`;
    }
    const isLoggedIn = localStorage.getItem("DfsWeb.isLoggedIn") === "true";
    if (routeTo === `${window.contextPath || ""}/home` && !isLoggedIn) {
      routeTo = `${window.contextPath || "/"}`;
    }
    return routeTo;
  };

  const handleNavigation = (routeTo, state, hash) => {
    const isTrackSchemePage = pathnames.includes("trackScheme");
    const navOptions = { state, ...(hash ? { hash } : {}) };
    if (isTrackSchemePage) {
      props.handleService?.({});
    } else {
      navigate(routeTo, navOptions);
    }
  };

  const handleStackCrumbClick = (index) => {
    const entry = stackEntries[index];
    if (!entry) return;
    if (index === stackEntries.length - 1) return;
    // Don't pop here — `handleNavigation` routes through the guarded
    // navigate, which may open a leave-confirmation dialog instead of
    // navigating immediately. Mutating the stack before that resolves
    // would desync the trail from the current page if the user cancels.
    // BreadcrumbStackContext's own location-effect trims the stack once
    // navigation actually completes (same mechanism the Back pill already
    // relies on below).
    handleNavigation(entry.route, entry.state, entry.hash);
  };

  // "Back" = up one level in the trail (reuses the same guards as the crumbs),
  // unless the caller supplies an explicit onBack handler.
  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    if (renderFromStack) {
      const parent = stack?.getParent?.();
      if (parent) {
        handleNavigation(parent.route, parent.state, parent.hash);
        return;
      }
    }
    const parentIndex = pathnames.length - 2;
    if (parentIndex < 0) {
      navigate("/");
      return;
    }
    const parentSegment = pathnames[parentIndex];
    const state = parentSegment === "details" ? detailsState : {};
    handleNavigation(buildRoute(parentIndex), state);
  };

  return (
    <Box
      role="presentation"
      id="main-content"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "16px",
        width: "100%",
        ...sx,
      }}
    >
      {showBack && (
        <Button
          onClick={handleBack}
          disableElevation
          variant="backPill"
          startIcon={<KeyboardBackspaceIcon />}
        >
          {t("COMMON_BACK")}
        </Button>
      )}
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={
          <Box
            component="span"
            sx={{
              color: "text.textGrey",
              fontSize: "14px",
              lineHeight: "normal",
            }}
          >
            ›
          </Box>
        }
        sx={{
          // Figma 639-103557: 6px between each crumb and the "›" separator
          // (MUI's default is 8px), items vertically centred against the pill.
          "& .MuiBreadcrumbs-separator": { mx: "6px" },
          "& .MuiBreadcrumbs-ol": { alignItems: "center" },
        }}
      >
        {renderFromStack
          ? stackEntries.map((entry, index) => {
              const isLast = index === stackEntries.length - 1 && extraCrumbLabels.length === 0;
              const label = t(entry.labelKey);
              return isLast ? (
                <Typography
                  key={`stack-${entry.id}-${index}`}
                  sx={{ ...CRUMB_TEXT_SX, color: currentColor, fontWeight: 400 }}
                >
                  {label}
                </Typography>
              ) : (
                <Link
                  key={`stack-${entry.id}-${index}`}
                  component={RouterLink}
                  to={entry.route}
                  underline="hover"
                  onClick={(e) => {
                    e.preventDefault();
                    handleStackCrumbClick(index);
                  }}
                  sx={{
                    ...CRUMB_TEXT_SX,
                    color: linkColor,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </Link>
              );
            })
          : pathnames.map((segment, index) => {
          // `hideSegments` drops a URL segment from the rendered trail
          // entirely (e.g. Assets' "assets-details" — the detail page's crumb
          // is super-category > sub-category, not a literal "Details" node)
          // while `buildRoute`/`handleBack` still see the real path.
          if (hideSegments.includes(segment)) return null;
          // When extraCrumbLabel is supplied, the last URL-derived segment is
          // no longer the trail's end — it becomes a clickable crumb and
          // extraCrumbLabel takes over as the final (non-clickable) one. Lets
          // a page whose category lives in a query param (e.g. /assets-section
          // ?cat=1), not a path segment, still show a 3rd "current" crumb
          // (Figma node 625-3980: Home › Assets › Storage) without inventing a
          // per-category route.
          const isLast = index === pathnames.length - 1 && extraCrumbLabels.length === 0;
          const routeTo = buildRoute(index);
          const state = segment === "details" ? detailsState : {};
          const label = getLabel(segment, index);
          return isLast ? (
            <Typography
              key={`${segment}-${index}`}
              sx={{ ...CRUMB_TEXT_SX, color: currentColor, fontWeight: 400 }}
            >
              {label}
            </Typography>
          ) : (
            <Link
              key={`${segment}-${index}`}
              component={RouterLink}
              to={routeTo}
              underline="hover"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(routeTo, state);
              }}
              sx={{
                ...CRUMB_TEXT_SX,
                color: linkColor,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {label}
            </Link>
          );
        })}
        {extraCrumbLabels.map((crumb, index) => {
          const isCurrent = index === extraCrumbLabels.length - 1 || !crumb.to;
          if (isCurrent) {
            return (
              <Typography
                key={`extra-${index}`}
                sx={{ ...CRUMB_TEXT_SX, color: currentColor, fontWeight: 400 }}
              >
                {crumb.label}
              </Typography>
            );
          }
          return (
            <Link
              key={`extra-${index}`}
              component={RouterLink}
              to={crumb.to}
              underline="hover"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(crumb.to, {});
              }}
              sx={{
                ...CRUMB_TEXT_SX,
                color: linkColor,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

RewampBreadCrumbsBar.propTypes = {
  /** When true, appended to the crumb trail (legacy scheme-tracking case). */
  trackScheme: PropTypes.arrayOf(PropTypes.string),
  /** Reset handler invoked instead of navigating while on a trackScheme page. */
  handleService: PropTypes.func,
  /** Scheme-details router state (folded in for the "details" crumb). */
  schemeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  active: PropTypes.bool,
  mainSchemeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Show the green Back pill (default true). */
  showBack: PropTypes.bool,
  /** Override the default "up one level" Back behaviour. */
  onBack: PropTypes.func,
  /** Extra sx merged onto the outer row — the page owns vertical spacing. */
  sx: PropTypes.object,
  /**
   * Appends trailing crumbs after the URL-derived trail (already-localized
   * labels). String entries are non-clickable; `{ label, to }` is a link
   * unless it is the last crumb. Used when "current" state lives in a query
   * param / router state — e.g. Assets list (`?cat=`) or detail
   * (super-category link > sub-category, with `hideSegments`).
   */
  extraCrumbLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ label: PropTypes.string, to: PropTypes.string }),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ label: PropTypes.string, to: PropTypes.string }),
      ])
    ),
  ]),
  /** URL segments to omit from the rendered trail entirely (route-building/
   * Back are unaffected — only display). E.g. Assets' "assets-details". */
  hideSegments: PropTypes.arrayOf(PropTypes.string),
  /** When false, always use URL-derived crumbs even if a stack exists. */
  useStackTrail: PropTypes.bool,
};

export default RewampBreadCrumbsBar;
